import axios from 'axios';
import { Config } from '@/config';
import { ApiService } from '@/services/api.service';

export const BASE_URL = `${Config.api.businessIntelligence.url}api/v1/`;

const CancelToken = axios.CancelToken;
let cancelTokens = {
  summary: null,
  tender: null,
  competitorSummary: null,
  tendersBidded: null,
  competitors: null,
  emitters: null,
  emitter: null,
  emitterSuggestion: null,
  normalizedNameSuggestion: null,
  tenantNormalizedNameSuggestion: null,
};

const toPagedResult = (response) => ({
  data: response.data.data,
  pagination: {
    page: response.data.page,
    totalCount: response.data.total_count,
    totalPages: response.data.total_pages,
  },
});

// aloha.bi's EmitterRequest: aliases travel as `normalized_names`
const toEmitterRequest = ({ name, normalizedNames }) => ({
  name,
  normalized_names: normalizedNames || [],
});

class BusinessIntelligenceService {
  getCharts(type) {
    return ApiService.get(`${Config.api.bi.url}api/v1/charts/get_chart?chart_type=${type}`).then(
      (response) => response.data,
    );
  }
  getPerformanceSummary(startDate, endDate) {
    if (cancelTokens.summary) {
      cancelTokens.summary.cancel('Multiple requests.');
    }
    cancelTokens.summary = CancelToken.source();
    return ApiService.get(`${BASE_URL}tender/summary?start_date=${startDate}&end_date=${endDate}`).then(
      (response) => response.data,
    );
  }
  getCompetitorPerformanceSummary(competitorId, startDate, endDate) {
    if (cancelTokens.competitorSummary) {
      cancelTokens.competitorSummary.cancel('Multiple requests.');
    }
    cancelTokens.competitorSummary = CancelToken.source();
    return ApiService.get(
      `${BASE_URL}competitor/${competitorId}/tender/summary?start_date=${startDate}&end_date=${endDate}`,
    ).then((response) => response.data);
  }
  getTendersBidded(startDate, endDate, status, page, sorting_field, sorting_order) {
    if (cancelTokens.tendersBidded) {
      cancelTokens.tendersBidded.cancel('Multiple requests.');
    }
    cancelTokens.tendersBidded = CancelToken.source();

    const statusQuery = status ? `&status=${status}` : '';

    const dateRangeQuery = `start_date=${startDate}&end_date=${endDate}`;
    const paginationQuery = `page=${page}&sorting_field=${sorting_field}&sorting_order=${sorting_order}`;
    const queryParams = `${dateRangeQuery}&${paginationQuery}${statusQuery}`;

    return ApiService.get(`${BASE_URL}tender/bidded?${queryParams}`, {
      cancelToken: cancelTokens.tendersBidded.token,
    }).then((response) => {
      const data = response.data.data;
      const pagination = {
        page: response.data.page,
        totalCount: response.data.total_count,
        totalPages: response.data.total_pages,
      };
      return { data, pagination };
    });
  }
  getCompetitorTendersBidded(competitorId, startDate, endDate, status, page, sorting_field, sorting_order) {
    if (cancelTokens.tendersBidded) {
      cancelTokens.tendersBidded.cancel('Multiple requests.');
    }
    cancelTokens.tendersBidded = CancelToken.source();

    const statusQuery = status ? `&status=${status}` : '';
    const dateRangeQuery = `start_date=${startDate}&end_date=${endDate}`;
    const paginationQuery = `page=${page}&sorting_field=${sorting_field}&sorting_order=${sorting_order}`;

    const queryParams = `${dateRangeQuery}&${paginationQuery}${statusQuery}`;

    return ApiService.get(`${BASE_URL}competitor/${competitorId}/tender/bidded?${queryParams}`, {
      cancelToken: cancelTokens.tendersBidded.token,
    }).then((response) => {
      const data = response.data.data;
      const pagination = {
        page: response.data.page,
        totalCount: response.data.total_count,
        totalPages: response.data.total_pages,
      };
      return { data, pagination };
    });
  }
  getCompetitors(page, sortingField, sortingOrder) {
    if (cancelTokens.competitors) {
      cancelTokens.competitors.cancel('Multiple requests.');
    }
    cancelTokens.competitors = CancelToken.source();

    return ApiService.get(
      `${BASE_URL}competitor?page=${page}&sorting_field=${sortingField}&sorting_order=${sortingOrder}`,
      {
        cancelToken: cancelTokens.competitors.token,
      },
    ).then((response) => {
      const data = response.data.data;
      const pagination = {
        page: response.data.page,
        totalCount: response.data.total_count,
        totalPages: response.data.total_pages,
      };
      return { data, pagination };
    });
  }
  // Emitters: the shared catalogue of procuring entities (aloha.bi /api/v1/emitter)
  getEmitters(page, sortingField, sortingOrder, search = '') {
    if (cancelTokens.emitters) {
      cancelTokens.emitters.cancel('Multiple requests.');
    }
    cancelTokens.emitters = CancelToken.source();

    const searchQuery = search && search.trim() ? `&search=${encodeURIComponent(search.trim())}` : '';

    return ApiService.get(
      `${BASE_URL}emitter?page=${page}&sorting_field=${sortingField}&sorting_order=${sortingOrder}${searchQuery}`,
      {
        cancelToken: cancelTokens.emitters.token,
      },
    ).then(toPagedResult);
  }
  getEmitter(emitterId) {
    // a newer fetch supersedes the previous one, so a slow response can't land in the store last
    if (cancelTokens.emitter) {
      cancelTokens.emitter.cancel('Multiple requests.');
    }
    cancelTokens.emitter = CancelToken.source();

    return ApiService.get(`${BASE_URL}emitter/${emitterId}`, {
      cancelToken: cancelTokens.emitter.token,
    }).then((response) => response.data);
  }
  createEmitter(emitter) {
    return ApiService.post(`${BASE_URL}emitter`, toEmitterRequest(emitter)).then((response) => response.data);
  }
  updateEmitter(emitterId, emitter) {
    return ApiService.put(`${BASE_URL}emitter/${emitterId}`, toEmitterRequest(emitter)).then(
      (response) => response.data,
    );
  }
  deleteEmitter(emitterId) {
    return ApiService.delete(`${BASE_URL}emitter/${emitterId}`).then((response) => response.data);
  }
  getEmitterPerformanceSummary(emitterId, startDate, endDate) {
    if (cancelTokens.emitterSummary) {
      cancelTokens.emitterSummary.cancel('Multiple requests.');
    }
    cancelTokens.emitterSummary = CancelToken.source();
    return ApiService.get(
      `${BASE_URL}emitter/${emitterId}/tender/summary?start_date=${startDate}&end_date=${endDate}`,
      {
        cancelToken: cancelTokens.emitterSummary.token,
      },
    ).then((response) => response.data);
  }
  getEmitterTenders(emitterId, startDate, endDate, status, page, sorting_field, sorting_order) {
    if (cancelTokens.tendersBidded) {
      cancelTokens.tendersBidded.cancel('Multiple requests.');
    }
    cancelTokens.tendersBidded = CancelToken.source();

    const statusQuery = status ? `&status=${status}` : '';
    const dateRangeQuery = `start_date=${startDate}&end_date=${endDate}`;
    const paginationQuery = `page=${page}&sorting_field=${sorting_field}&sorting_order=${sorting_order}`;
    const queryParams = `${dateRangeQuery}&${paginationQuery}${statusQuery}`;

    return ApiService.get(`${BASE_URL}emitter/${emitterId}/tender?${queryParams}`, {
      cancelToken: cancelTokens.tendersBidded.token,
    }).then(toPagedResult);
  }
  // Procuring entity names from the tender data that resemble `name`, to pick aliases from
  getEmitterNameSuggestion(name, limit = 10) {
    if (cancelTokens.emitterSuggestion) {
      cancelTokens.emitterSuggestion.cancel('Multiple requests.');
    }
    cancelTokens.emitterSuggestion = CancelToken.source();

    return ApiService.get(
      `${BASE_URL}emitter/suggestion?name=${encodeURIComponent((name || '').trim())}&limit=${limit}`,
      { cancelToken: cancelTokens.emitterSuggestion.token },
    ).then((response) => response.data);
  }
  cancelEmitterNameSuggestion() {
    if (cancelTokens.emitterSuggestion) {
      cancelTokens.emitterSuggestion.cancel('Cancel request.');
    }
  }
  getTenantNormalizedNameSuggestion() {
    if (cancelTokens.tenantNormalizedNameSuggestion) {
      cancelTokens.tenantNormalizedNameSuggestion.cancel('Multiple requests.');
    }
    cancelTokens.tenantNormalizedNameSuggestion = CancelToken.source();

    return ApiService.get(`${BASE_URL}normalized-name/suggestion/tenant`, {
      cancelToken: cancelTokens.tenantNormalizedNameSuggestion.token,
    }).then((response) => response.data);
  }
  getNormalizedNameSuggestion(name) {
    if (cancelTokens.normalizedNameSuggestion) {
      cancelTokens.normalizedNameSuggestion.cancel('Multiple requests.');
    }
    cancelTokens.normalizedNameSuggestion = CancelToken.source();

    return ApiService.get(`${BASE_URL}normalized-name/suggestion?name=${name}`, {
      cancelToken: cancelTokens.normalizedNameSuggestion.token,
    }).then((response) => response.data);
  }
  cancelNormalizedNameSuggestion() {
    if (cancelTokens.normalizedNameSuggestion) {
      cancelTokens.normalizedNameSuggestion.cancel('Cancel request.');
    }
  }
  getTenderById(tenderId, competitorId) {
    if (cancelTokens.tender) {
      cancelTokens.tender.cancel('Multiple requests.');
    }
    cancelTokens.tender = CancelToken.source();
    if (competitorId) {
      return ApiService.get(`${BASE_URL}tender/${tenderId}?competitor_id=${competitorId}`).then(
        (response) => response.data,
      );
    }
    return ApiService.get(`${BASE_URL}tender/${tenderId}`).then((response) => response.data);
  }
}

export const businessIntelligenceService = new BusinessIntelligenceService();
